import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, Check, Lock } from "lucide-react";

const STAGES = ["Idea / Pre-seed", "Seed", "Series A", "Growth / Scale-up", "Established SME"];
const INTERESTS = [
  { value: "cfo", label: "CFO / Strategic Finance" },
  { value: "ai", label: "AI Consulting" },
  { value: "investor_materials", label: "Investor Materials" },
  { value: "other", label: "Other" },
];

const initial = {
  name: "",
  email: "",
  company: "",
  phone: "",
  role: "",
  company_stage: "",
  interest: "",
  preferred_time: "",
  message: "",
};

export default function BookingForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", form);
      toast.success(data.message || "Booking received.");
      setDone(true);
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="booking-form-success"
        className="surface p-10 md:p-12 text-center"
      >
        <div className="mx-auto w-12 h-12 border border-gold/40 flex items-center justify-center">
          <Check className="w-5 h-5 text-gold" />
        </div>
        <h3 className="mt-6 text-2xl font-display font-light">Booking received</h3>
        <p className="mt-3 text-zinc-400 max-w-md mx-auto">
          We&rsquo;ll review your context and respond within one business day to
          confirm a 15-minute window that works.
        </p>
        <button
          onClick={() => setDone(false)}
          className="btn-ghost mt-8"
          data-testid="booking-form-reset"
        >
          Send another
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="booking-form"
      className="surface p-8 md:p-10 space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Full name" required>
          <input
            data-testid="booking-name"
            type="text"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            required
            className="form-input"
          />
        </Field>
        <Field label="Email" required>
          <input
            data-testid="booking-email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            required
            className="form-input"
          />
        </Field>
        <Field label="Company">
          <input
            data-testid="booking-company"
            type="text"
            value={form.company}
            onChange={(e) => update("company", e.target.value)}
            className="form-input"
          />
        </Field>
        <Field label="Role / title">
          <input
            data-testid="booking-role"
            type="text"
            value={form.role}
            onChange={(e) => update("role", e.target.value)}
            className="form-input"
          />
        </Field>
        <Field label="Phone (optional)">
          <input
            data-testid="booking-phone"
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            className="form-input"
          />
        </Field>
        <Field label="Preferred time">
          <input
            data-testid="booking-preferred-time"
            type="text"
            placeholder="e.g. Weekday mornings, AEDT"
            value={form.preferred_time}
            onChange={(e) => update("preferred_time", e.target.value)}
            className="form-input"
          />
        </Field>
        <Field label="Company stage">
          <select
            data-testid="booking-stage"
            value={form.company_stage}
            onChange={(e) => update("company_stage", e.target.value)}
            className="form-input"
          >
            <option value="">Select…</option>
            {STAGES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Primary interest">
          <select
            data-testid="booking-interest"
            value={form.interest}
            onChange={(e) => update("interest", e.target.value)}
            className="form-input"
          >
            <option value="">Select…</option>
            {INTERESTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="What's the one decision you'd like to discuss?">
        <textarea
          data-testid="booking-message"
          rows={5}
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          className="form-input resize-y"
          placeholder="e.g. Capital raise positioning, 18-month cash plan, AI rollout for finance ops…"
        />
      </Field>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
        <p className="text-xs text-zinc-500 max-w-md leading-relaxed flex items-start gap-2">
          <Lock className="w-3 h-3 mt-0.5 text-gold flex-shrink-0" />
          <span>
            By submitting you agree to be contacted about your enquiry. Your
            details are stored encrypted, never sold, and handled per our{" "}
            <Link to="/trust" className="text-zinc-300 hover:text-gold underline-offset-2 hover:underline">
              Trust & Privacy
            </Link>{" "}
            policy.
          </span>
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="booking-submit"
          className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed min-w-[220px]"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Sending…
            </>
          ) : (
            "Book a 15-min Strategy Call"
          )}
        </button>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          background: #0a0a0a;
          border: 1px solid rgba(255,255,255,0.08);
          color: #fff;
          font-family: 'Manrope', sans-serif;
          font-size: 14px;
          padding: 12px 14px;
          outline: none;
          transition: border-color .2s ease, background .2s ease;
          border-radius: 0;
        }
        .form-input:focus { border-color: rgba(212,175,55,0.55); background: #0c0c0c; }
        .form-input::placeholder { color: rgba(255,255,255,0.25); }
        select.form-input { appearance: none; background-image: linear-gradient(45deg, transparent 50%, #D4AF37 50%), linear-gradient(135deg, #D4AF37 50%, transparent 50%); background-position: calc(100% - 18px) 50%, calc(100% - 12px) 50%; background-size: 6px 6px; background-repeat: no-repeat; padding-right: 36px; }
      `}</style>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] tracking-[0.18em] uppercase text-zinc-500 mb-2">
        {label} {required && <span className="text-gold">*</span>}
      </span>
      {children}
    </label>
  );
}
